import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { App as AntApp, ConfigProvider } from "antd";
import { store } from "./store";
import { registerToastApis } from "./utils/toast";
import Login from "./pages/Auth/Login";
import Register from "./pages/Auth/Register";
import Dashboard from "./pages/Dashboard/Dashboard";
import Suppliers from "./pages/Suppliers/Suppliers";
import MainLayout from "./components/Layout/MainLayout";
import StockManagement from "./pages/StockManagement";
import Billing from "./pages/Billing/Billing";
import Patients from "./pages/Patients/Patients";
import Reports from "./pages/Reports/Reports";
import Medicines from "./pages/Medicines/Medicines";
import Purchases from "./pages/Purchases/Purchases";
import PurchaseForm from "./pages/Purchases/PurchaseForm";
import SettingsPage from "./pages/Settings/Settings";
import UsersPage from "./pages/Users/Users";
import ProfilePage from "./pages/Profile/Profile";
import BillPrint from "./pages/Billing/BillPrint";
import ProtectedRoute from "./components/ProtectedRoute";
import "antd/dist/reset.css";
import "./App.css";

/** Registers App-scoped message/notification so axios + pages can toast reliably. */
const ToastBridge: React.FC = () => {
  const { message, notification } = AntApp.useApp();

  useEffect(() => {
    registerToastApis(message, notification);
  }, [message, notification]);

  return null;
};

const withLayout = (node: React.ReactNode) => <MainLayout>{node}</MainLayout>;

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#0d7a6f",
            colorLink: "#0d7a6f",
            colorInfo: "#0d7a6f",
            borderRadius: 6,
            fontSize: 13,
            controlHeight: 32,
            controlHeightSM: 28,
            padding: 12,
            paddingLG: 16,
            paddingMD: 12,
            paddingSM: 8,
            paddingXS: 4,
            marginLG: 16,
            marginMD: 12,
            marginSM: 8,
            colorBgLayout: "#f4f6f8",
            colorBorderSecondary: "#e8ecf0",
          },
          components: {
            Layout: {
              headerHeight: 48,
              siderBg: "#0f1c2e",
              triggerBg: "#0f1c2e",
            },
            Menu: {
              darkItemBg: "transparent",
              darkSubMenuItemBg: "transparent",
              darkItemSelectedBg: "rgba(13, 122, 111, 0.35)",
              darkItemHoverBg: "rgba(255,255,255,0.06)",
              itemHeight: 36,
              iconSize: 15,
            },
            Card: {
              paddingLG: 12,
              headerHeight: 40,
            },
            Table: {
              cellPaddingBlock: 8,
              cellPaddingInline: 10,
              headerBorderRadius: 6,
            },
            Form: {
              itemMarginBottom: 12,
              verticalLabelPadding: "0 0 4px",
            },
            Tabs: {
              horizontalItemGutter: 20,
              horizontalMargin: "0 0 8px 0",
              titleFontSize: 13,
            },
            Modal: {
              paddingMD: 16,
              titleFontSize: 15,
            },
            Button: {
              paddingInline: 12,
            },
            Statistic: {
              contentFontSize: 20,
              titleFontSize: 12,
            },
          },
        }}
      >
        <AntApp notification={{ placement: "topRight", top: 24, duration: 3 }}>
          <ToastBridge />
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              <Route path="/billing/:id/print" element={<ProtectedRoute />}>
                <Route path="" element={<BillPrint />} />
              </Route>

              <Route path="/" element={<ProtectedRoute />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={withLayout(<Dashboard />)} />
                <Route path="/medicines" element={withLayout(<Medicines />)} />
                <Route path="/stock" element={withLayout(<StockManagement />)} />
                <Route path="/patients" element={withLayout(<Patients />)} />
                <Route path="/billing" element={withLayout(<Billing />)} />
                <Route path="/suppliers" element={withLayout(<Suppliers />)} />
                <Route path="/profile" element={withLayout(<ProfilePage />)} />
              </Route>

              <Route
                path="/"
                element={<ProtectedRoute roles={["admin", "manager"]} />}
              >
                <Route path="/purchases" element={withLayout(<Purchases />)} />
                <Route
                  path="/purchases/new"
                  element={withLayout(<PurchaseForm />)}
                />
                <Route path="/reports" element={withLayout(<Reports />)} />
                <Route path="/settings" element={withLayout(<SettingsPage />)} />
              </Route>

              <Route path="/" element={<ProtectedRoute roles={["admin"]} />}>
                <Route path="/users" element={withLayout(<UsersPage />)} />
              </Route>
            </Routes>
          </Router>
        </AntApp>
      </ConfigProvider>
    </Provider>
  );
};

export default App;
